import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigurationService, OidcSecurityService, OpenIdConfiguration } from 'angular-auth-oidc-client';
import { Observable, switchMap } from 'rxjs';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  private readonly configurationService: ConfigurationService = inject(ConfigurationService);
  private readonly securityService: OidcSecurityService = inject(OidcSecurityService);

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const allConfigurations: OpenIdConfiguration[] = this.configurationService.getAllConfigurations();
    const allRoutesConfigured: string[][] = allConfigurations.map(x => x.secureRoutes || []);
    const allRoutesConfiguredFlat: string[] = allRoutesConfigured.flat();

    const { matchingConfig, matchingRoute } = this.getConfigIdForClosestMatchingRoute(req.url, allConfigurations);

    return this.securityService.getIdToken(matchingConfig?.configId).pipe(
      switchMap(token => {
        if (!matchingConfig) {
          console.debug(`Did not find any configured route for route ${req.url}`);
          return next.handle(req);
        }

        if (token) {
          if (allRoutesConfiguredFlat.length === 0) {
            console.debug('No routes have been configured');
            return next.handle(req);
          } else {
            console.debug(`'${req.url}' matches configured route '${matchingRoute}'`);

            req = req.clone({
              setHeaders: {
                Authorization: `Bearer ${token}`,
              },
            });

            return next.handle(req);
          }
        } else {
          console.debug('No token found, redirecting to login page');

          return next.handle(req);
        }
      }),
    );
  }

  private getConfigIdForClosestMatchingRoute(
    route: string,
    configurations: OpenIdConfiguration[],
  ): {
    matchingRoute: string | null;
    matchingConfig: OpenIdConfiguration | null;
  } {
    for (const config of configurations) {
      const { secureRoutes } = config;

      for (const configuredRoute of secureRoutes ?? []) {
        if (route.startsWith(configuredRoute)) {
          return {
            matchingRoute: configuredRoute,
            matchingConfig: config,
          };
        }
      }
    }

    return { matchingRoute: null, matchingConfig: null };
  }
}
